'use client';

import { type MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckCircle2, ChevronLeft, ChevronRight, FileText, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Progress } from '@/components/ui/progress';
import {
  Dialog,
  DialogContent,
  DialogFooter,
} from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { ResumeComparisonPanel, type ResumeComparisonChange } from './ResumeComparisonDialog';
import { ResumeCreateDialog } from './ResumeCreateDialog';
import { ResumeListItem, type ResumeListItemData } from './ResumeListItem';
import { ResumeUploadCreateActions } from './ResumeUploadCreateActions';
import { ResumeUploadDialog } from './ResumeUploadDialog';

const PAGE_SIZE = 3;

type ResumeSourceType = 'uploaded' | 'builder';
type RefineSourceType = 'RESUME' | 'NOTE' | 'RAW' | 'JOB' | 'COMPANY' | 'PROFILE' | 'FILE';
type RefineFlowStage =
  | 'idle'
  | 'duplicating'
  | 'attaching-context'
  | 'running-refine'
  | 'finalizing'
  | 'done'
  | 'error';

type SelectorResume = ResumeListItemData & {
  sourceType: ResumeSourceType;
  viewUrl: string;
  isDraft: boolean;
};

interface RefineFlowResult {
  resumeId: string;
  resumeName: string;
  editUrl: string;
  viewUrl: string;
  sourceResumeId: string;
  sourceResumeType: ResumeSourceType;
}

export interface ResumeRefineContextSource {
  sourceId?: string;
  type: RefineSourceType;
  title: string;
  description?: string;
  contentText?: string;
  contentJson?: Record<string, unknown>;
}

interface UploadedResumeApiItem {
  id: string;
  title?: string;
  fileName?: string;
  filename?: string;
  fileSize?: number;
  fileMime?: string;
  createdAt?: string;
  updatedAt?: string;
  uploadedAt?: string;
  url?: string;
  linkedBuilderReportId?: string | null;
}

interface BuilderResumeApiItem {
  id: string;
  name?: string;
  version?: number;
  status?: string;
  createdAt?: string;
  updatedAt?: string;
}

interface ResumeSelectorProps {
  selectedResumeId?: string;
  onSelect: (resumeId: string | undefined) => void;
  onResumeMetaChange?: (resume: ResumeListItemData | null) => void;
  /** Fires whenever the initial resume-list fetch starts/finishes, so callers can
   *  show a resolving state rather than a premature "nothing selected" flash. */
  onLoadingChange?: (loading: boolean) => void;
  refineContextSources?: ResumeRefineContextSource[];
  onRefinedResumeCreated?: (resumeId: string) => void;
  autoRefreshIntervalMs?: number;
  /**
   * When true, and nothing is selected once resumes have loaded, pre-select the
   * most-recently-updated resume as a fallback default. Runs at most once and
   * never overrides an existing (persisted or user-made) selection.
   */
  autoSelectLatestWhenEmpty?: boolean;
  /**
   * Only meaningful together with `autoSelectLatestWhenEmpty`. When true, the
   * fallback default prefers the most-recently-updated NON-DRAFT resume (skipping
   * unfinished Resume Builder drafts). If every resume is a draft, it falls back
   * to the newest overall so a resume is still selected when one exists.
   */
  preferNonDraft?: boolean;
  className?: string;
}

function toIsoOrNow(value?: string): string {
  const parsed = value ? new Date(value) : new Date();
  return Number.isNaN(parsed.getTime()) ? new Date().toISOString() : parsed.toISOString();
}

function sortByRecent(a: SelectorResume, b: SelectorResume): number {
  const aTime = new Date(a.updatedAt || a.createdAt).getTime();
  const bTime = new Date(b.updatedAt || b.createdAt).getTime();
  return bTime - aTime;
}

function mapUploadedResume(item: UploadedResumeApiItem): SelectorResume {
  const fileName = item.fileName || item.filename || item.title || 'Uploaded resume';
  const createdAt = toIsoOrNow(item.createdAt || item.uploadedAt);
  const updatedAt = toIsoOrNow(item.updatedAt || item.createdAt || item.uploadedAt);

  return {
    id: item.id,
    title: item.title || fileName,
    fileName,
    fileSize: item.fileSize || 0,
    fileMime: item.fileMime || 'application/octet-stream',
    createdAt,
    updatedAt,
    sourceType: 'uploaded',
    viewUrl: item.url || '',
    isDraft: false,
  };
}

function mapBuilderResume(item: BuilderResumeApiItem): SelectorResume {
  const createdAt = toIsoOrNow(item.createdAt);
  const updatedAt = toIsoOrNow(item.updatedAt || item.createdAt);
  const versionLabel = item.version ? `v${item.version}` : 'Builder';
  const statusLabel = item.status ? ` • ${item.status}` : '';
  const fileName = `Resume Builder ${versionLabel}${statusLabel}`;

  return {
    id: item.id,
    title: item.name || 'Untitled Resume',
    fileName,
    fileSize: 0,
    fileMime: 'application/json',
    createdAt,
    updatedAt,
    sourceType: 'builder',
    viewUrl: `/d/pitch/resumes/builder/${item.id}`,
    isDraft: item.status === 'DRAFT',
  };
}

function toCompactText(value: string, maxLength = 56): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength - 1).trimEnd()}...`;
}

function buildRefineTargetLabel(sources: ResumeRefineContextSource[]): string {
  const jobs = sources
    .filter((source) => source.type === 'JOB')
    .map((source) => source.title.trim())
    .filter((title) => title.length > 0);
  const companies = sources
    .filter((source) => source.type === 'COMPANY')
    .map((source) => source.title.trim())
    .filter((title) => title.length > 0);

  const primaryJob = jobs[0];
  const primaryCompany = companies[0];

  if (primaryJob && primaryCompany) {
    return toCompactText(`${primaryJob} @ ${primaryCompany}`);
  }
  if (primaryJob) {
    return toCompactText(primaryJob);
  }
  if (primaryCompany) {
    return toCompactText(primaryCompany);
  }
  return '';
}

function buildRefinedResumeName(baseTitle: string, targetLabel: string): string {
  const normalizedBase =
    baseTitle.trim().replace(/\s-\sRefined(?:\s-\s.*)?$/i, '') || 'Resume';
  return targetLabel.length > 0
    ? `${normalizedBase} - Refined - ${targetLabel}`
    : `${normalizedBase} - Refined`;
}

function buildRefinementContextSummary(sources: ResumeRefineContextSource[]): string | undefined {
  if (sources.length === 0) {
    return undefined;
  }
  const compact = sources
    .slice(0, 3)
    .map((source) => `${source.type}: ${source.title}`)
    .join(' | ');
  return toCompactText(compact, 120);
}

function getRefineStageProgress(stage: RefineFlowStage): number {
  if (stage === 'duplicating') return 20;
  if (stage === 'attaching-context') return 45;
  if (stage === 'running-refine') return 75;
  if (stage === 'finalizing') return 92;
  if (stage === 'done') return 100;
  return 0;
}

export function ResumeSelector({
  selectedResumeId,
  onSelect,
  onResumeMetaChange,
  onLoadingChange,
  refineContextSources,
  onRefinedResumeCreated,
  autoRefreshIntervalMs = 0,
  autoSelectLatestWhenEmpty = false,
  preferNonDraft = false,
  className,
}: ResumeSelectorProps) {
  const [resumes, setResumes] = useState<SelectorResume[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [refiningResumeId, setRefiningResumeId] = useState<string | null>(null);
  const [isRefineDialogOpen, setIsRefineDialogOpen] = useState(false);
  const [refineFlowStage, setRefineFlowStage] = useState<RefineFlowStage>('idle');
  const [refineFlowError, setRefineFlowError] = useState<string | null>(null);
  const [refineSourceResumeTitle, setRefineSourceResumeTitle] = useState<string>('');
  const [refineFlowResult, setRefineFlowResult] = useState<RefineFlowResult | null>(null);
  const [isLoadingRefineComparison, setIsLoadingRefineComparison] = useState(false);
  const [refineComparisonError, setRefineComparisonError] = useState<string | null>(null);
  const [refineComparisonChanges, setRefineComparisonChanges] = useState<ResumeComparisonChange[]>([]);
  const [hasLoadedRefineComparison, setHasLoadedRefineComparison] = useState(false);
  const [isRefineComparisonExpanded, setIsRefineComparisonExpanded] = useState(false);
  const [pendingDeleteResumeId, setPendingDeleteResumeId] = useState<string | null>(null);
  const [deletingResumeId, setDeletingResumeId] = useState<string | null>(null);
  const didAutoSelectRef = useRef(false);
  const { toast } = useToast();

  const loadResumes = useCallback(
    async (focusResumeId?: string, options?: { silent?: boolean }) => {
      if (!options?.silent) {
        setIsLoading(true);
      }
      try {
        const [uploadedResponse, builderResponse] = await Promise.all([
          fetch('/api/settings/resumes', { cache: 'no-store' }),
          fetch('/api/resume-builder', { cache: 'no-store' }),
        ]);

        const uploadedPayload = (await uploadedResponse.json().catch(() => null)) as
          | { resumes?: UploadedResumeApiItem[]; error?: string }
          | null;
        const builderPayload = (await builderResponse.json().catch(() => null)) as
          | { resumes?: BuilderResumeApiItem[]; error?: string }
          | null;

        if (!uploadedResponse.ok && !builderResponse.ok) {
          throw new Error(
            uploadedPayload?.error || builderPayload?.error || 'Failed to load resumes'
          );
        }

        const uploadedResumesRaw = Array.isArray(uploadedPayload?.resumes)
          ? uploadedPayload!.resumes
          : [];
        const builderResumesRaw = Array.isArray(builderPayload?.resumes)
          ? builderPayload!.resumes
          : [];

        const builderIds = new Set(builderResumesRaw.map((resume) => resume.id));
        const unlinkedOrOrphanUploaded = uploadedResumesRaw.filter(
          (resume) =>
            !resume.linkedBuilderReportId || !builderIds.has(resume.linkedBuilderReportId)
        );

        const uploadedResumes = unlinkedOrOrphanUploaded.map(mapUploadedResume);
        const builderResumes = builderResumesRaw.map(mapBuilderResume);
        const merged = [...uploadedResumes, ...builderResumes].sort(sortByRecent);
        setResumes(merged);

        if (focusResumeId) {
          const focusIndex = merged.findIndex((resume) => resume.id === focusResumeId);
          if (focusIndex >= 0) {
            setCurrentPage(Math.floor(focusIndex / PAGE_SIZE));
          }
          return;
        }

        setCurrentPage((prev) => {
          const maxPage = Math.max(0, Math.ceil(merged.length / PAGE_SIZE) - 1);
          return Math.min(prev, maxPage);
        });
      } catch (error) {
        console.error('Failed to load resumes in selector:', error);
        if (!options?.silent) {
          toast({
            variant: 'destructive',
            title: 'Error',
            description: error instanceof Error ? error.message : 'Failed to load resumes',
          });
        }
      } finally {
        if (!options?.silent) {
          setIsLoading(false);
        }
      }
    },
    [toast]
  );

  useEffect(() => {
    void loadResumes();
  }, [loadResumes]);

  useEffect(() => {
    if (!autoRefreshIntervalMs || autoRefreshIntervalMs < 1000) {
      return;
    }

    const intervalId = window.setInterval(() => {
      if (document.visibilityState !== 'visible' || refiningResumeId) {
        return;
      }
      void loadResumes(selectedResumeId, { silent: true });
    }, autoRefreshIntervalMs);

    return () => window.clearInterval(intervalId);
  }, [autoRefreshIntervalMs, loadResumes, refiningResumeId, selectedResumeId]);

  useEffect(() => {
    if (!onResumeMetaChange) return;
    const selectedResume = selectedResumeId
      ? resumes.find((resume) => resume.id === selectedResumeId) || null
      : null;
    onResumeMetaChange(selectedResume);
  }, [onResumeMetaChange, resumes, selectedResumeId]);

  useEffect(() => {
    onLoadingChange?.(isLoading);
  }, [onLoadingChange, isLoading]);

  useEffect(() => {
    if (!pendingDeleteResumeId) return;
    const exists = resumes.some((resume) => resume.id === pendingDeleteResumeId);
    if (!exists) {
      setPendingDeleteResumeId(null);
    }
  }, [pendingDeleteResumeId, resumes]);

  // Fallback default: once resumes have loaded and nothing is selected, pre-select
  // a sensible default. `resumes` is already sorted newest-first by `sortByRecent`
  // (max updatedAt, createdAt fallback), so `resumes[0]` is the genuine latest
  // across the full list — not just the current page. When `preferNonDraft` is set
  // we pick the newest resume that is not a Resume Builder DRAFT, falling back to
  // the newest overall if every resume is a draft. Guarded to run at most once so
  // it never overrides a persisted default or a manual (de)selection made later in
  // the session.
  useEffect(() => {
    if (!autoSelectLatestWhenEmpty || didAutoSelectRef.current || isLoading) {
      return;
    }
    didAutoSelectRef.current = true;
    if (selectedResumeId || resumes.length === 0) {
      return;
    }
    const preferred = preferNonDraft
      ? resumes.find((resume) => !resume.isDraft)
      : undefined;
    onSelect((preferred ?? resumes[0]).id);
  }, [autoSelectLatestWhenEmpty, isLoading, onSelect, preferNonDraft, resumes, selectedResumeId]);

  const totalPages = Math.max(1, Math.ceil(resumes.length / PAGE_SIZE));
  const clampedPage = Math.min(currentPage, totalPages - 1);
  const startIndex = clampedPage * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, resumes.length);

  const visibleResumes = useMemo(
    () => resumes.slice(startIndex, endIndex),
    [endIndex, resumes, startIndex]
  );
  const normalizedRefineContextSources = useMemo(
    () =>
      (refineContextSources ?? [])
        .filter((source) => source.type !== 'RESUME')
        .filter((source) => source.title.trim().length > 0)
        .map((source) => ({
          sourceId: source.sourceId,
          type: source.type,
          title: source.title.trim(),
          description: source.description?.trim(),
          contentText: source.contentText,
          contentJson: source.contentJson,
        })),
    [refineContextSources]
  );
  const refinedTargetLabel = useMemo(
    () => buildRefineTargetLabel(normalizedRefineContextSources),
    [normalizedRefineContextSources]
  );
  const refinementContextItems = useMemo(
    () =>
      normalizedRefineContextSources.map((source) => ({
        type: source.type,
        title: source.title,
      })),
    [normalizedRefineContextSources]
  );
  const refinementContextSummary = useMemo(
    () => buildRefinementContextSummary(normalizedRefineContextSources),
    [normalizedRefineContextSources]
  );
  const isRefineFlowRunning =
    refineFlowStage === 'duplicating' ||
    refineFlowStage === 'attaching-context' ||
    refineFlowStage === 'running-refine' ||
    refineFlowStage === 'finalizing';
  const refineProgressValue = getRefineStageProgress(refineFlowStage);

  const handleSelect = (resumeId: string) => {
    onSelect(selectedResumeId === resumeId ? undefined : resumeId);
  };

  const deleteResume = useCallback(
    async (resume: SelectorResume) => {
      if (deletingResumeId) {
        return;
      }

      setDeletingResumeId(resume.id);
      try {
        const response = await fetch(
          resume.sourceType === 'uploaded'
            ? `/api/settings/resumes/${resume.id}`
            : `/api/resume-builder/${resume.id}`,
          {
            method: 'DELETE',
          }
        );
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        if (!response.ok) {
          throw new Error(payload?.error || 'Failed to delete resume');
        }

        if (selectedResumeId === resume.id) {
          onSelect(undefined);
        }

        await loadResumes(undefined, { silent: true });
        setPendingDeleteResumeId(null);
        toast({
          title: 'Deleted',
          description: 'Resume deleted successfully.',
        });
      } catch (error) {
        toast({
          variant: 'destructive',
          title: 'Delete failed',
          description: error instanceof Error ? error.message : 'Failed to delete resume',
        });
      } finally {
        setDeletingResumeId(null);
      }
    },
    [deletingResumeId, loadResumes, onSelect, selectedResumeId, toast]
  );

  const handleViewResume = (
    event: MouseEvent<HTMLButtonElement>,
    resume: SelectorResume
  ) => {
    event.stopPropagation();
    if (!resume.viewUrl) {
      toast({
        variant: 'destructive',
        title: 'Unable to open resume',
        description: 'No view URL is available for this resume.',
      });
      return;
    }
    window.open(resume.viewUrl, '_blank', 'noopener,noreferrer');
  };

  const handleDeleteResume = useCallback(
    async (event: MouseEvent<HTMLButtonElement>, resume: SelectorResume) => {
      event.stopPropagation();
      if (deletingResumeId) {
        return;
      }

      if (pendingDeleteResumeId !== resume.id) {
        setPendingDeleteResumeId(resume.id);
        return;
      }

      await deleteResume(resume);
    },
    [deleteResume, deletingResumeId, pendingDeleteResumeId]
  );

  const renameResumeCopy = useCallback(async (resumeId: string, nextName: string) => {
    const response = await fetch(`/api/resume-builder/${resumeId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name: nextName }),
    });
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    if (!response.ok) {
      throw new Error(payload?.error || 'Failed to name refined resume copy');
    }
  }, []);

  const createBuilderRefineCopy = useCallback(
    async (resume: SelectorResume, refinedName: string): Promise<string> => {
      const response = await fetch(`/api/resume-builder/${resume.id}/version`, {
        method: 'POST',
      });
      const payload = (await response.json().catch(() => null)) as
        | { resume?: { id?: string }; error?: string }
        | null;
      const createdResumeId = payload?.resume?.id;
      if (!response.ok || typeof createdResumeId !== 'string' || createdResumeId.length === 0) {
        throw new Error(payload?.error || 'Failed to duplicate selected resume');
      }

      await renameResumeCopy(createdResumeId, refinedName);
      return createdResumeId;
    },
    [renameResumeCopy]
  );

  const createUploadedRefineCopy = useCallback(async (resume: SelectorResume, refinedName: string): Promise<string> => {
    const createdResponse = await fetch('/api/resume-builder', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        name: refinedName,
      }),
    });
    const createdPayload = (await createdResponse.json().catch(() => null)) as
      | { resume?: { id?: string }; error?: string }
      | null;
    const createdResumeId = createdPayload?.resume?.id;
    if (!createdResponse.ok || typeof createdResumeId !== 'string' || createdResumeId.length === 0) {
      throw new Error(createdPayload?.error || 'Failed to create a refine copy');
    }

    const linkResponse = await fetch(`/api/resume-builder/${createdResumeId}/sources`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sourceId: resume.id,
        type: 'RESUME',
        title: resume.title,
        description: 'Original uploaded resume selected for refinement.',
      }),
    });
    const linkPayload = (await linkResponse.json().catch(() => null)) as { error?: string } | null;
    if (!linkResponse.ok) {
      throw new Error(linkPayload?.error || 'Failed to attach the original resume to refine copy');
    }

    const fillResponse = await fetch(`/api/resume-builder/${createdResumeId}/fill-from-sources`, {
      method: 'POST',
    });
    if (!fillResponse.ok) {
      console.error('Failed to prefill uploaded refine copy from sources', { resumeId: createdResumeId });
    }

    return createdResumeId;
  }, []);

  const attachRefineContextSources = useCallback(
    async (resumeId: string) => {
      for (const source of normalizedRefineContextSources) {
        const response = await fetch(`/api/resume-builder/${resumeId}/sources`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceId: source.sourceId,
            type: source.type,
            title: source.title,
            description: source.description,
            contentText: source.contentText,
            contentJson: source.contentJson,
          }),
        });
        const payload = (await response.json().catch(() => null)) as { error?: string } | null;
        if (!response.ok) {
          throw new Error(payload?.error || `Failed to attach source "${source.title}"`);
        }
      }
    },
    [normalizedRefineContextSources]
  );

  const runRefineCopy = useCallback(async (resumeId: string) => {
    const response = await fetch(`/api/resume-builder/${resumeId}/refine`, {
      method: 'POST',
    });
    const payload = (await response.json().catch(() => null)) as { error?: string } | null;
    if (!response.ok) {
      throw new Error(payload?.error || 'Failed to run resume refinement');
    }
  }, []);

  const saveRefinementMetadata = useCallback(
    async (
      resumeId: string,
      meta: {
        status: 'PENDING' | 'COMPLETED' | 'FAILED';
        sourceResumeId: string;
        sourceResumeType: ResumeSourceType;
        refinedAt?: string;
      }
    ) => {
      try {
        const response = await fetch(`/api/resume-builder/${resumeId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            settings: {
              refinement: {
                isRefined: true,
                status: meta.status,
                refinedAt: meta.refinedAt,
                sourceResumeId: meta.sourceResumeId,
                sourceResumeType: meta.sourceResumeType,
                contextSummary: refinementContextSummary,
                contextItems: refinementContextItems,
              },
            },
          }),
        });

        if (!response.ok) {
          const payload = (await response.json().catch(() => null)) as { error?: string } | null;
          console.error('Failed to persist refinement metadata', {
            resumeId,
            status: meta.status,
            error: payload?.error,
          });
        }
      } catch (error) {
        console.error('Failed to persist refinement metadata', { resumeId, status: meta.status, error });
      }
    },
    [refinementContextItems, refinementContextSummary]
  );

  const handleRefineResume = useCallback(
    async (event: MouseEvent<HTMLButtonElement>, resume: SelectorResume) => {
      event.stopPropagation();
      if (refiningResumeId) {
        return;
      }

      setRefineFlowError(null);
      setRefineFlowResult(null);
      setRefineComparisonError(null);
      setRefineComparisonChanges([]);
      setHasLoadedRefineComparison(false);
      setIsRefineComparisonExpanded(false);
      setRefineSourceResumeTitle(resume.title);
      setIsRefineDialogOpen(true);
      setRefiningResumeId(resume.id);
      setRefineFlowStage('duplicating');

      let refinedResumeId: string | null = null;
      try {
        const refinedName = buildRefinedResumeName(resume.title, refinedTargetLabel);
        refinedResumeId =
          resume.sourceType === 'builder'
            ? await createBuilderRefineCopy(resume, refinedName)
            : await createUploadedRefineCopy(resume, refinedName);

        await saveRefinementMetadata(refinedResumeId, {
          status: 'PENDING',
          sourceResumeId: resume.id,
          sourceResumeType: resume.sourceType,
        });

        if (normalizedRefineContextSources.length > 0) {
          setRefineFlowStage('attaching-context');
          await attachRefineContextSources(refinedResumeId);
        }

        setRefineFlowStage('running-refine');
        await runRefineCopy(refinedResumeId);

        setRefineFlowStage('finalizing');
        await saveRefinementMetadata(refinedResumeId, {
          status: 'COMPLETED',
          sourceResumeId: resume.id,
          sourceResumeType: resume.sourceType,
          refinedAt: new Date().toISOString(),
        });
        const refinedResumeUrl = new URL(
          `/d/pitch/resumes/builder/${refinedResumeId}`,
          window.location.origin
        );

        await loadResumes(refinedResumeId, { silent: true });
        setRefineFlowResult({
          resumeId: refinedResumeId,
          resumeName: refinedName,
          editUrl: refinedResumeUrl.toString(),
          viewUrl: refinedResumeUrl.toString(),
          sourceResumeId: resume.id,
          sourceResumeType: resume.sourceType,
        });
        setRefineFlowStage('done');
      } catch (error) {
        if (refinedResumeId) {
          await saveRefinementMetadata(refinedResumeId, {
            status: 'FAILED',
            sourceResumeId: resume.id,
            sourceResumeType: resume.sourceType,
          });
        }
        console.error('Failed to start refine flow from resume selector:', error);
        setRefineFlowError(
          error instanceof Error ? error.message : 'Failed to prepare refined resume'
        );
        setRefineFlowStage('error');
      } finally {
        setRefiningResumeId(null);
      }
    },
    [
      attachRefineContextSources,
      createBuilderRefineCopy,
      createUploadedRefineCopy,
      loadResumes,
      normalizedRefineContextSources.length,
      saveRefinementMetadata,
      refinedTargetLabel,
      refiningResumeId,
      runRefineCopy,
    ]
  );

  const refineStepItems = useMemo(
    () => [
      {
        key: 'duplicate',
        label: 'Create refined copy',
        isVisible: true,
      },
      {
        key: 'context',
        label: 'Attach context sources',
        isVisible: normalizedRefineContextSources.length > 0,
      },
      {
        key: 'refine',
        label: 'Run AI refinement',
        isVisible: true,
      },
      {
        key: 'finalize',
        label: 'Finalize refined resume',
        isVisible: true,
      },
    ],
    [normalizedRefineContextSources.length]
  );

  const resetRefineDialog = useCallback(() => {
    setIsRefineDialogOpen(false);
    setRefineFlowStage('idle');
    setRefineFlowError(null);
    setRefineFlowResult(null);
    setRefineSourceResumeTitle('');
    setIsLoadingRefineComparison(false);
    setRefineComparisonError(null);
    setRefineComparisonChanges([]);
    setHasLoadedRefineComparison(false);
    setIsRefineComparisonExpanded(false);
  }, []);

  const handleCloseRefineDialog = useCallback(() => {
    if (isRefineFlowRunning) {
      return;
    }
    resetRefineDialog();
  }, [isRefineFlowRunning, resetRefineDialog]);

  const handleOpenRefinedResume = useCallback((url: string) => {
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleLoadRefineComparison = useCallback(async () => {
    if (!refineFlowResult || isLoadingRefineComparison) {
      return;
    }

    if (refineFlowResult.sourceResumeType !== 'builder') {
      setRefineComparisonError(
        'Change comparison is available when refining from a Resume Builder resume.'
      );
      setRefineComparisonChanges([]);
      setHasLoadedRefineComparison(true);
      return;
    }

    setIsLoadingRefineComparison(true);
    setHasLoadedRefineComparison(false);
    setIsRefineComparisonExpanded(true);
    setRefineComparisonError(null);
    try {
      const response = await fetch('/api/resume-builder/refine/changes', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          oldResumeId: refineFlowResult.sourceResumeId,
          refinedResumeId: refineFlowResult.resumeId,
        }),
      });
      const payload = (await response.json().catch(() => null)) as
        | { error?: string; changes?: ResumeComparisonChange[] }
        | null;
      if (!response.ok) {
        throw new Error(payload?.error || 'Failed to load changes');
      }

      setRefineComparisonChanges(Array.isArray(payload?.changes) ? payload!.changes : []);
      setHasLoadedRefineComparison(true);
    } catch (error) {
      setRefineComparisonError(
        error instanceof Error ? error.message : 'Failed to load comparison details'
      );
      setRefineComparisonChanges([]);
      setHasLoadedRefineComparison(true);
    } finally {
      setIsLoadingRefineComparison(false);
    }
  }, [isLoadingRefineComparison, refineFlowResult]);

  const handleUseRefinedResume = useCallback(() => {
    if (!refineFlowResult) {
      return;
    }
    onSelect(refineFlowResult.resumeId);
    onRefinedResumeCreated?.(refineFlowResult.resumeId);
    resetRefineDialog();
  }, [onRefinedResumeCreated, onSelect, refineFlowResult, resetRefineDialog]);

  const getRefineStepState = useCallback(
    (
      stepKey: 'duplicate' | 'context' | 'refine' | 'finalize'
    ): 'pending' | 'active' | 'completed' => {
      const stageOrder: Record<
        'duplicating' | 'attaching-context' | 'running-refine' | 'finalizing' | 'done',
        number
      > = {
        duplicating: 1,
        'attaching-context': 2,
        'running-refine': 3,
        finalizing: 4,
        done: 5,
      };

      const currentOrder = stageOrder[refineFlowStage as keyof typeof stageOrder] ?? 0;
      const stepOrder =
        stepKey === 'duplicate'
          ? 1
          : stepKey === 'context'
            ? 2
            : stepKey === 'refine'
              ? 3
              : 4;

      if (stepKey === 'context' && normalizedRefineContextSources.length === 0) {
        return 'completed';
      }

      if (currentOrder === stepOrder) {
        return 'active';
      }

      if (currentOrder > stepOrder) {
        return 'completed';
      }

      return 'pending';
    },
    [normalizedRefineContextSources.length, refineFlowStage]
  );

  if (isLoading) {
    return (
      <div className="border border-border rounded p-4 bg-muted/20">
        <p className="text-sm text-muted-foreground text-center">Loading resumes...</p>
      </div>
    );
  }

  if (resumes.length === 0) {
    return (
      <>
        <div className="border border-dashed border-border rounded p-6 text-center">
          <FileText className="h-10 w-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-foreground font-medium mb-2">No resumes found</p>
          <p className="text-xs text-muted-foreground mb-4">
            Upload a resume or create one with Resume Builder.
          </p>
          <ResumeUploadCreateActions
            onUpload={() => setUploadDialogOpen(true)}
            onCreate={() => setCreateDialogOpen(true)}
            className="justify-center"
          />
        </div>

        <ResumeUploadDialog
          open={uploadDialogOpen}
          onOpenChange={setUploadDialogOpen}
          onUploaded={() => loadResumes()}
        />
        <ResumeCreateDialog
          open={createDialogOpen}
          onOpenChange={setCreateDialogOpen}
          disableNavigation
          onCreated={(resumeId) => {
            onSelect(resumeId);
            void loadResumes(resumeId);
          }}
        />
      </>
    );
  }

  return (
    <>
      <div className={className ?? 'space-y-3 min-w-0'}>
        <div className="grid min-w-0 gap-2">
          {visibleResumes.map((resume) => (
            <ResumeListItem
              key={resume.id}
              resume={resume}
              isSelected={selectedResumeId === resume.id}
              onSelect={() => handleSelect(resume.id)}
              onView={(event) => handleViewResume(event, resume)}
              onRefine={(event) => void handleRefineResume(event, resume)}
              onDelete={(event) => void handleDeleteResume(event, resume)}
              isDeleteConfirm={pendingDeleteResumeId === resume.id}
              isDeleting={deletingResumeId === resume.id}
              isRefining={refiningResumeId === resume.id}
            />
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-xs text-muted-foreground">
            Showing {startIndex + 1}-{endIndex} of {resumes.length}
          </p>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 border sm:flex-none"
              onClick={() => setCurrentPage((prev) => Math.max(0, prev - 1))}
              disabled={clampedPage === 0}
            >
              <ChevronLeft className="h-4 w-4" />
              Prev
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="flex-1 border sm:flex-none"
              onClick={() => setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))}
              disabled={clampedPage >= totalPages - 1}
            >
              Next
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <ResumeUploadCreateActions
          onUpload={() => setUploadDialogOpen(true)}
          onCreate={() => setCreateDialogOpen(true)}
        />
      </div>

      <Dialog
        open={isRefineDialogOpen}
        onOpenChange={(nextOpen) => {
          if (!nextOpen) {
            handleCloseRefineDialog();
            return;
          }
          setIsRefineDialogOpen(true);
        }}
      >
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeading
            title="Refining Resume"
            description={<>Refining{' '}<span className="font-medium text-foreground">{refineSourceResumeTitle || 'selected resume'}</span>{refinedTargetLabel ? <>{' '}for{' '}<span className="font-medium text-foreground">{refinedTargetLabel}</span>.</> : '.'}</>}
          />

          <div className="rounded-md border border-border bg-muted/20 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Context
            </p>
            {normalizedRefineContextSources.length > 0 ? (
              <div className="mt-2 flex flex-wrap gap-2">
                {normalizedRefineContextSources.map((source, index) => (
                  <span
                    key={`${source.type}-${source.title}-${index}`}
                    className="inline-flex items-center gap-1 rounded border border-border bg-background px-2 py-1 text-xs"
                  >
                    <span className="text-muted-foreground">{source.type}</span>
                    <span className="text-foreground">{source.title}</span>
                  </span>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">
                No context was preselected. You can add sources manually after opening the resume.
              </p>
            )}
          </div>

          {(isRefineFlowRunning || refineFlowStage === 'done') && (
            <div className="space-y-4">
              <Progress value={refineProgressValue} />
              <div className="space-y-2">
                {refineStepItems
                  .filter((step) => step.isVisible)
                  .map((step) => {
                    const state = getRefineStepState(
                      step.key as 'duplicate' | 'context' | 'refine' | 'finalize'
                    );
                    return (
                      <div key={step.key} className="flex items-center gap-2 text-sm">
                        {state === 'completed' ? (
                          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                        ) : state === 'active' ? (
                          <Loader2 className="h-4 w-4 animate-spin text-primary" />
                        ) : (
                          <span className="h-4 w-4 rounded-full border border-border" />
                        )}
                        <span
                          className={cn(
                            state === 'active' && 'text-foreground',
                            state === 'completed' && 'text-muted-foreground',
                            state === 'pending' && 'text-muted-foreground'
                          )}
                        >
                          {step.label}
                        </span>
                      </div>
                    );
                  })}
              </div>
            </div>
          )}

          {refineFlowStage === 'done' && refineFlowResult && (
            <div className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-4 py-3 text-sm">
              <p className="font-medium text-foreground">Refined resume is ready</p>
              <p className="mt-1 text-muted-foreground">{refineFlowResult.resumeName}</p>
            </div>
          )}

          {refineFlowStage === 'done' && refineFlowResult && (
            <ResumeComparisonPanel
              open={isRefineComparisonExpanded}
              onOpenChange={setIsRefineComparisonExpanded}
              changes={refineComparisonChanges}
              isLoading={isLoadingRefineComparison}
              error={refineComparisonError}
              hasLoaded={hasLoadedRefineComparison}
              loadingMessage="Comparing old and refined resumes..."
              idleMessage={
                <>
                  Click <span className="font-medium text-foreground">What&apos;s changed</span> to
                  compare the source and refined resumes.
                </>
              }
            />
          )}

          {refineFlowStage === 'error' && (
            <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {refineFlowError || 'Failed to prepare refined resume.'}
            </div>
          )}

          <DialogFooter className="gap-2 sm:justify-between sm:space-x-0">
            {refineFlowStage === 'done' && refineFlowResult ? (
              <>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenRefinedResume(refineFlowResult.viewUrl)}
                  >
                    View
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleOpenRefinedResume(refineFlowResult.editUrl)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => void handleLoadRefineComparison()}
                    disabled={isLoadingRefineComparison}
                  >
                    {isLoadingRefineComparison ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Checking...
                      </>
                    ) : (
                      "What's changed"
                    )}
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={handleUseRefinedResume}>
                    Use this resume
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCloseRefineDialog}>
                    Done
                  </Button>
                </div>
              </>
            ) : refineFlowStage === 'error' ? (
              <div className="flex w-full justify-end">
                <Button type="button" variant="outline" onClick={handleCloseRefineDialog}>
                  Close
                </Button>
              </div>
            ) : (
              <div className="flex w-full justify-end">
                <Button type="button" variant="outline" disabled>
                  Preparing...
                </Button>
              </div>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ResumeUploadDialog
        open={uploadDialogOpen}
        onOpenChange={setUploadDialogOpen}
        onUploaded={() => loadResumes()}
      />
      <ResumeCreateDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        disableNavigation
        onCreated={(resumeId) => {
          onSelect(resumeId);
          void loadResumes(resumeId);
        }}
      />
    </>
  );
}
